// Botón de audio compartido por los dos formatos de escucha del banco.
//
// Spec visual del documento (Parte II §2):
//   · "Botón central circular grande (80-100px) con ondas de audio animadas
//      mientras suena."
//   · "Sin transcripción visible — texto de la pregunta aparece solo después de
//      que termina el audio o tras 1 replay."
//   · "Permitir 1 replay máximo (simula condición real de escucha, sin ser
//      injusto)."
//
// El audio se genera con Web Speech API (Punto de Decisión B — ver
// ../onboardingSpeech.js para la justificación técnica completa).
import { useEffect, useRef, useState } from 'react';
import { speakSlide, cancelSpeech, isSpeechAvailable } from '../onboardingSpeech';

const MAX_REPLAYS = 1;

export default function AudioPlayer({ slide, onFinished }) {
  const [playing, setPlaying] = useState(false);
  const [replays, setReplays] = useState(0);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const finishedRef = useRef(false);

  // Corta el audio al desmontar o al cambiar de slide.
  useEffect(() => {
    finishedRef.current = false;
    return () => cancelSpeech();
  }, [slide.id]);

  const play = () => {
    if (playing) return;
    if (hasPlayed && replays >= MAX_REPLAYS) return;

    if (!isSpeechAvailable()) {
      // Degradación honesta: si el navegador no puede hablar, revelamos el
      // texto para que el alumno no quede bloqueado en la calibración.
      setUnavailable(true);
      setHasPlayed(true);
      if (!finishedRef.current) { finishedRef.current = true; onFinished?.(); }
      return;
    }

    if (hasPlayed) setReplays((r) => r + 1);

    speakSlide(slide.audio, slide.voice, {
      onStart: () => setPlaying(true),
      onEnd: () => {
        setPlaying(false);
        setHasPlayed(true);
        if (!finishedRef.current) { finishedRef.current = true; onFinished?.(); }
      },
      onError: () => {
        setPlaying(false);
        setUnavailable(true);
        setHasPlayed(true);
        if (!finishedRef.current) { finishedRef.current = true; onFinished?.(); }
      },
    });
  };

  const replaysLeft = MAX_REPLAYS - replays;
  const canReplay = hasPlayed && replaysLeft > 0 && !playing;

  return (
    <div className="ob-audio-wrap">
      <button
        type="button"
        className={`ob-audio-btn${playing ? ' ob-playing' : ''}`}
        onClick={play}
        disabled={playing || (hasPlayed && replaysLeft <= 0)}
        aria-label={playing ? 'Reproduciendo audio' : 'Reproducir audio'}
      >
        <span className="ob-audio-wave" aria-hidden="true" />
        <span className="ob-audio-wave" aria-hidden="true" />
        <span className="ob-audio-wave" aria-hidden="true" />
        <span aria-hidden="true">{playing ? '🔊' : '▶'}</span>
      </button>

      {!hasPlayed && !playing && (
        <p className="ob-microcopy">Toca para escuchar</p>
      )}

      {canReplay && (
        <button type="button" className="ob-replay" onClick={play}>
          Escuchar otra vez ({replaysLeft} disponible)
        </button>
      )}

      {hasPlayed && replaysLeft <= 0 && !playing && !unavailable && (
        <p className="ob-microcopy">Ya usaste tu repetición — confía en tu oído.</p>
      )}

      {/* Solo si el navegador no puede sintetizar voz. En condiciones normales
          el texto NUNCA se muestra: el documento exige forzar oído real. */}
      {unavailable && (
        <p className="ob-microcopy">
          Tu navegador no puede reproducir el audio, así que te lo dejamos escrito:
          <br />
          <strong>“{slide.audio}”</strong>
        </p>
      )}
    </div>
  );
}
